import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Title } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Title);

const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { position: 'top' as const },
        title: { display: true, text: 'Weekly Bookings' },
    },
    scales: {
        x: { title: { display: true, text: 'Week' } },
        y: { title: { display: true, text: 'Number of Bookings' } },
    },
};

const LineChart = ({ bookings }: { bookings: any[] }) => {
    const { labels, dataCounts } = useMemo(() => {
        const bookingsPerWeek: Record<string, number> = {};
        const currentDate = new Date();
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        const filteredBookings = bookings.filter((booking: { created_at: string }) => {
            const bookingDate = new Date(booking.created_at);
            return bookingDate >= startOfMonth && bookingDate <= endOfMonth;
        });

        filteredBookings.forEach((booking: { created_at: string }) => {
            const bookingDate = new Date(booking.created_at);
            const startOfWeek = new Date(bookingDate);
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            const fmt = (date: Date) =>
                `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;

            const weekRange = `${fmt(startOfWeek)} to ${fmt(endOfWeek)}`;
            bookingsPerWeek[weekRange] = (bookingsPerWeek[weekRange] || 0) + 1;
        });

        const sortedWeeks = Object.keys(bookingsPerWeek).sort((a, b) => {
            const parse = (r: string) => new Date(r.split(' to ')[0].split('-').reverse().join('-'));
            return parse(a).getTime() - parse(b).getTime();
        });

        return { labels: sortedWeeks, dataCounts: sortedWeeks.map(w => bookingsPerWeek[w]) };
    }, [bookings]);

    return (
        <div className="w-full rounded-xl border bg-card p-3 lg:p-4">
            <p className="text-base lg:text-lg font-semibold">Booking Trends</p>
            <div className="mt-3 h-72 lg:h-80">
                <Line
                    data={{
                        labels,
                        datasets: [{
                            label: 'Bookings',
                            data: dataCounts,
                            fill: false,
                            borderColor: 'rgba(75, 192, 192, 1)',
                            tension: 0.1,
                        }],
                    }}
                    options={options}
                />
            </div>
        </div>
    );
};

export default LineChart;
